import { ChangeDetectionStrategy, Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CrmService } from '../../crm.service';
import { CrmQuotation, CrmQuotationAttachment, CrmQuotationExternalReview, CrmQuotationJobType, CrmQuotationRequirement, CrmQuotationReviewNote, CrmQuotationStatus, CrmQuotationTask } from '../../crm.model';
import { ToolbarService } from 'src/app/services/toolbar.service';
import { WorkflowPanelComponent } from 'src/app/workflow/workflow-panel/workflow-panel.component';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { RippleModule } from 'primeng/ripple';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PanelModule } from 'primeng/panel';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ProgressBarModule } from 'primeng/progressbar';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { FirebaseStorageService } from '../../../services/firebase-storage.service';
import { QuotationContractLink } from '../quotation-contract-conversion.model';
import { OrdersService } from '../../../order/order.service';
import { SalesOrder } from '../../../order/sales-order/sales-order.model';
import { WarehousingMasterData } from '../../../warehousing/warehousing.model';
import { WarehousingService } from '../../../warehousing/warehousing.service';
import { Customer } from '../../../customer/customer.model';
import { CustomerService } from '../../../customer/customer.service';
import { DeliveryRateCard } from '../../../contracts/models/delivery-rate-card.model';
import { SessionStorageService } from '../../../services/session-storage.service';
import { UOM } from '../../../models/meta-params/uom';
import { City } from '../../../models/common/city.model';
import { Country } from '../../../models/common/country.model';
import { BaseMasterDataService } from '../../../services/base-master-data.service';
import { environment } from '../../../../environments/environment';
import { LogisticsProjectsService } from '../../../logistics-projects/logistics-projects.service';

type SourceType = 'CLIENT' | 'PROSPECT' | 'LEAD';

interface SourceOption {
  label: string;
  value: string;
  type: SourceType;
  id?: number;
  record?: any;
}

interface LogisticsRateCardRow {
  rateId?: number;
  sourceCity?: string;
  sourceCountry?: string;
  destinationCity?: string;
  destinationCountry?: string;
  vehicleTypeName?: string;
  vehicleConfigName?: string;
  vehicleCapacityName?: string;
  amount?: number;
  distance?: number;
  waitingThreshold?: number;
  waitingCharge?: number;
  goodsInsurance?: boolean;
  portPermit?: boolean;
  rateType?: string;
  unitBasis?: string;
  transitTime?: string;
}

@Component({
  selector: 'app-quotation-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    TagModule,
    PanelModule,
    BreadcrumbModule,
    TableModule,
    ToastModule,
    RippleModule,
    InputTextModule,
    SelectModule,
    DatePickerModule,
    ProgressBarModule,
    DialogModule,
    TextareaModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    InputNumberModule,
    PageShellComponent,
    PageDetailHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    WorkflowPanelComponent
  ],
  providers: [BaseMasterDataService, MessageService],
  templateUrl: './quotation-details.component.html',
  styleUrls: ['./quotation-details.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuotationDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private crmService = inject(CrmService);
  private messageService = inject(MessageService);
  private fb = inject(FormBuilder);
  private firebaseStorageService = inject(FirebaseStorageService);
  private ordersService = inject(OrdersService);
  private warehousingService = inject(WarehousingService);
  private customerService = inject(CustomerService);
  private sessionStorageService = inject(SessionStorageService);
  private logisticsProjectsService = inject(LogisticsProjectsService);
  private masterDataService = inject(BaseMasterDataService);
  private loadedCityCountryIds = new Set<number>();
  private loadingCityCountryIds = new Set<number>();

  public toolbarService = inject(ToolbarService);

  public quotation = signal<CrmQuotation | null>(null);
  public uomOptions = signal<{ label: string; value: string }[]>([]);
  public requirements = signal<CrmQuotationRequirement[]>([]);
  public tasks = signal<CrmQuotationTask[]>([]);
  public attachments = signal<CrmQuotationAttachment[]>([]);
  public reviewNotes = signal<CrmQuotationReviewNote[]>([]);
  public externalReviews = signal<CrmQuotationExternalReview[]>([]);
  public contractLinks = signal<QuotationContractLink[]>([]);
  public salesOrderLinks = signal<SalesOrder[]>([]);
  public brandOptions = signal<WarehousingMasterData[]>([]);
  public sourceOptions = signal<SourceOption[]>([]);
  public customerOptions = signal<Customer[]>([]);
  public contactResolving = signal(false);
  public cities = signal<City[]>([]);
  public countries = signal<Country[]>([]);
  public brandDialogVisible = signal(false);
  public savingBrand = signal(false);
  public contactDialogVisible = signal(false);
  public savingContact = signal(false);
  public editingContact = signal<Customer | null>(null);
  public selectedAttachmentFile = signal<File | null>(null);
  public uploadingAttachment = signal(false);
  public pdfGenerating = signal(false);
  public loading = signal<boolean>(false);
  public cfHandoffVisible = signal(false);
  public cfHandoffLoading = signal(false);
  public cfHandoffExecuting = signal(false);
  public cfHandoffPreview = signal<any | null>(null);
  public paymentTerms = signal<any[]>([]);
  public homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
  public breadcrumbItems = [
    { label: 'CRM', routerLink: '/crm/quotations' },
    { label: 'Enquiry Details' }
  ];
  public quotationStatuses = Object.values(CrmQuotationStatus).map(s => ({ label: s, value: s }));
  public yesNoOptions = [{ label: 'Yes', value: true }, { label: 'No', value: false }];
  public activeTab = signal<string>('general');
  public tabs = [
    { id: 'general', label: 'General', icon: 'pi pi-info-circle' },
    { id: 'job', label: 'Job Details', icon: 'pi pi-briefcase' },
    { id: 'lines', label: 'Lines', icon: 'pi pi-list' },
    { id: 'terms', label: 'Terms', icon: 'pi pi-file' },
    { id: 'documents', label: 'Documents', icon: 'pi pi-paperclip' },
    { id: 'communication', label: 'Communication', icon: 'pi pi-send' },
    { id: 'remarks', label: 'Remarks', icon: 'pi pi-comments' },
    { id: 'tracking', label: 'Tracking', icon: 'pi pi-sitemap' }
  ];
  public latestGeneratedPdf = computed(() => this.attachments()
    .filter(attachment => attachment.generated && attachment.sourceType === 'CRM_QUOTATION_PDF')
    .sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0) || new Date(b.uploadedAt || 0).getTime() - new Date(a.uploadedAt || 0).getTime())[0] || null);
  public detailMetrics = computed(() => {
    const quote = this.quotation();
    if (!quote) {
      return [];
    }
    const lineCount = quote.lines?.length || this.lineControls.length || 0;
    const generatedPdf = this.latestGeneratedPdf();
    const workflowCount = this.requirements().length + this.tasks().length;

    return [
      {
        label: 'Quotation Value',
        value: `${quote.currencyCode || 'SAR'} ${Number(quote.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        hint: `${lineCount} line${lineCount === 1 ? '' : 's'}`,
        tone: 'success' as const
      },
      {
        label: 'Status',
        value: quote.status || '-',
        hint: quote.deviationStatus || 'NO_CRITERIA',
        tone: quote.status === 'REJECTED' ? 'danger' as const : quote.status === 'APPROVED' || quote.status === 'JOB' ? 'success' as const : 'info' as const
      },
      {
        label: 'Documents',
        value: this.attachments().length,
        hint: generatedPdf?.versionNumber ? `PDF v${generatedPdf.versionNumber}` : 'PDF pending',
        tone: this.attachments().length ? 'info' as const : 'warn' as const
      },
      {
        label: 'Workflow Items',
        value: workflowCount,
        hint: `${this.externalReviews().length} review link${this.externalReviews().length === 1 ? '' : 's'}`,
        tone: 'default' as const
      }
    ];
  });
  public logisticsRateCardRows = computed<LogisticsRateCardRow[]>(() => {
    const details = this.quotation()?.logisticsDetails;
    if (!details) {
      return [];
    }
    const rateCards = details.deliveryRateCards?.length
      ? details.deliveryRateCards
      : details.deliveryRateCard ? [details.deliveryRateCard as DeliveryRateCard] : [];
    return rateCards.map(rateCard => ({
        rateId: rateCard.rateId,
        sourceCity: rateCard.sourceCity || rateCard.sourceCityName,
        sourceCountry: rateCard.sourceCountry || rateCard.sourceCountryName,
        destinationCity: rateCard.destinationCity || rateCard.destinationCityName,
        destinationCountry: rateCard.destinationCountry || rateCard.destinationCountryName,
        vehicleTypeName: rateCard.vehicleTypeName,
        vehicleConfigName: rateCard.vehicleConfigName,
        vehicleCapacityName: rateCard.vehicleCapacityName,
        amount: rateCard.amount,
        distance: rateCard.distance,
        waitingThreshold: rateCard.waitingThreshold,
        waitingCharge: rateCard.waitingCharge,
        goodsInsurance: rateCard.goodsInsurance,
        portPermit: rateCard.portPermit,
        rateType: rateCard.rateType,
        unitBasis: rateCard.unitBasis,
        transitTime: rateCard.transitTime
    }));
  });
  public posmInstallationRows = computed(() => this.quotation()?.posmInstallationDetails?.rows || []);
  public storageRows = computed(() => this.quotation()?.storageDetails?.rows || []);

  // Rejection
  public showRejectDialog = signal(false);
  public rejectReason = signal('');
  public attachmentCategories = ['PROPOSAL', 'SOW', 'PRICING', 'OTHER'].map(value => ({ label: value, value }));
  public visibilityOptions = ['INTERNAL', 'CLIENT_VISIBLE'].map(value => ({ label: value, value }));
  public taskStatuses = ['OPEN', 'IN_PROGRESS', 'BLOCKED', 'COMPLETED', 'NOT_REQUIRED'].map(value => ({ label: value, value }));
  public categoryOptions = ['PRODUCT', 'SERVICE', 'EXTERNAL'].map(value => ({ label: value, value }));
  public priorityOptions = ['NORMAL', 'HIGH', 'URGENT'].map(value => ({ label: value, value }));
  public posmJobTypeOptions = ['INSTALLATION', 'REFURBISHMENT', 'LOGISTICS', 'OTHER'].map(value => ({ label: value, value }));

  public generalForm = this.fb.group({
    subject: ['', Validators.required],
    description: [''],
    countryId: [null as number | null],
    cityId: [null as number | null],
    sourceKey: [null as string | null, Validators.required],
    customerName: ['', Validators.required],
    customerId: [null as number | null],
    pocName: ['', Validators.required],
    pocEmail: [''],
    pocPhoneNumber: [''],
    pocMobileNumber: [''],
    currencyCode: ['SAR'],
    exchangeRate: [1],
    vatRate: [15],
    pricingNotes: [''],
    status: [CrmQuotationStatus.PREPARING]
  });

  public contactForm = this.fb.group({
    id: [null as number | null],
    name: ['', Validators.required],
    emailAddress: [''],
    phoneNumber: ['']
  });

  public linesForm = this.fb.array<FormGroup>([]);

  public requirementForm = this.fb.group({
    description: ['', Validators.required],
    sourceType: [''],
    sourceRecordId: [null as number | null],
    priority: ['NORMAL'],
    status: ['OPEN']
  });

  public taskForm = this.fb.group({
    title: ['', Validators.required],
    description: [''],
    assignedTeam: [''],
    assignedCustomerId: [null as number | null],
    dueDate: [null as Date | null],
    status: ['OPEN'],
    requiredForSubmission: [true]
  });

  public attachmentForm = this.fb.group({
    category: ['PROPOSAL', Validators.required],
    fileName: ['', Validators.required],
    fileUrl: [''],
    fileType: [''],
    fileSize: [null as number | null],
    visibility: ['CLIENT_VISIBLE']
  });

  public noteForm = this.fb.group({
    noteText: ['', Validators.required],
    visibility: ['INTERNAL'],
    stakeholderType: ['INTERNAL']
  });

  public submitForm = this.fb.group({
    recipientCustomerId: [null as number | null, Validators.required],
    recipientEmail: [''],
    expiresAt: [null as Date | null],
    comment: ['']
  });

  public posmForm = this.fb.group({
    projectName: ['', Validators.required],
    eventLocation: [''],
    brandId: [null as number | null],
    brandName: [''],
    jobPriority: ['NORMAL'],
    jobType: ['INSTALLATION']
  });

  public cfHandoffForm = this.fb.group({
    createSalesOrder: [false],
    paymentTermId: [null as number | null],
    overrideReason: [''],
    createClearance: [true],
    createContainerCase: [true],
    applyTemplate: [true],
    applyDocumentRequirements: [true]
  });

  public brandForm = this.fb.group({
    name: ['', Validators.required],
    code: ['']
  });

  ngOnInit() {
    this.uomOptions.set(this.toUomOptions(this.sessionStorageService.getUOMList() ?? []));
    this.countries.set(this.sessionStorageService.getCountryList() ?? []);
    this.loadBrands();
    this.loadSelectionOptions();
    this.ordersService.getAllPaymentTerms().subscribe(terms => this.paymentTerms.set(terms || []));
    this.generalForm.get('sourceKey')?.valueChanges.subscribe(value => this.applySource(value));
    this.generalForm.get('customerId')?.valueChanges.subscribe(value => this.applyContact(value));
    this.generalForm.get('countryId')?.valueChanges.subscribe(value => this.onGeneralCountryChange(value));
    this.submitForm.get('recipientCustomerId')?.valueChanges.subscribe(value => this.applySubmitRecipient(value));
    this.route.queryParamMap.subscribe(queryParams => {
      this.activeTab.set(this.resolveTab(queryParams.get('tab')));
    });
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadQuotation(parseInt(id));
    }
  }

  private toUomOptions(uoms: UOM[]) {
    const options = uoms
      .map(uom => {
        const value = uom.shortName || uom.name || '';
        const label = [uom.name, uom.shortName && uom.shortName !== uom.name ? uom.shortName : null].filter(Boolean).join(' - ');
        return value ? { label: label || value, value } : null;
      })
      .filter((option): option is { label: string; value: string } => !!option);
    return options.length ? options : ['EA', 'SQM', 'PALLET', 'TRIP'].map(value => ({ label: value, value }));
  }

  setTab(tabId: string) {
    const resolvedTab = this.resolveTab(tabId);
    this.activeTab.set(resolvedTab);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: resolvedTab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private resolveTab(tabId: string | null): string {
    return this.tabs.some(tab => tab.id === tabId) ? tabId! : 'general';
  }

  visibleTabs() {
    const quote = this.quotation();
    return this.tabs.filter(tab => tab.id !== 'job' || this.isPosmQuote(quote) || this.isLogisticsQuote(quote) || this.isStorageQuote(quote) || this.isCfQuote(quote));
  }

  isInvalid(form: FormGroup, controlName: string) {
    const control = form.get(controlName);
    return this.isControlInvalid(control);
  }

  isControlInvalid(control: AbstractControl | null | undefined) {
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  isBrandInvalid(controlName: string) {
    return this.isControlInvalid(this.brandForm.get(controlName));
  }

  isContactInvalid(controlName: string) {
    return this.isControlInvalid(this.contactForm.get(controlName));
  }

  isLineInvalid(index: number, controlName: string) {
    return this.isControlInvalid(this.linesForm.at(index)?.get(controlName));
  }

  get lineControls() {
    return this.linesForm.controls;
  }

  onBrandSelected(brandId: number | null) {
    const selectedBrand = this.brandOptions().find(brand => brand.id === brandId);
    this.posmForm.patchValue({ brandName: selectedBrand?.name || '' }, { emitEvent: false });
  }

  openBrandDialog() {
    this.brandForm.reset({ name: '', code: '' });
    this.brandDialogVisible.set(true);
  }

  closeBrandDialog() {
    if (this.savingBrand()) return;
    this.brandDialogVisible.set(false);
  }

  createBrand() {
    if (this.savingBrand()) {
      return;
    }
    if (this.brandForm.invalid) {
      this.brandForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Brand name is required.' });
      return;
    }
    const value = this.brandForm.value;
    this.savingBrand.set(true);
    this.warehousingService.saveMasterData({
      type: 'BRAND',
      name: (value.name || '').trim(),
      code: (value.code || '').trim() || undefined,
      isActive: true
    }).subscribe({
      next: response => {
        this.savingBrand.set(false);
        if (!response.success || !response.data) return;
        this.brandOptions.set([...this.brandOptions(), response.data].sort((a, b) => a.name.localeCompare(b.name)));
        this.posmForm.patchValue({ brandId: response.data.id ?? null, brandName: response.data.name }, { emitEvent: false });
        this.brandDialogVisible.set(false);
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Brand created successfully.' });
      },
      error: () => this.savingBrand.set(false)
    });
  }

  private loadBrands() {
    this.warehousingService.getMasterData('BRAND').subscribe(response => {
      this.brandOptions.set(response.data || []);
      this.syncBrandSelectionFromName();
    });
  }

  private loadSelectionOptions() {
    const companyId = this.toolbarService.customer()?.company?.id;
    if (!companyId) return;
    this.customerService.getCustomersByCompany(companyId).subscribe(customers => {
      this.customerOptions.set(customers || []);
      const quote = this.quotation();
      if (quote) {
        this.ensureSubmitRecipient(quote);
      }
    });
    this.crmService.searchClients({ companyId, size: 500, sortBy: 'companyName', sortDir: 'asc' }).subscribe(clients => {
      const clientOptions = (clients.content || []).map(record => ({
        label: `Client - ${[record.clientNumber, record.companyName].filter(Boolean).join(' - ') || record.id}`,
        value: `CLIENT:${record.id}`,
        type: 'CLIENT' as SourceType,
        id: record.id,
        record
      }));
      this.crmService.searchProspects({ companyId, size: 500, sortBy: 'companyName', sortDir: 'asc' }).subscribe(prospects => {
        const prospectOptions = (prospects.content || []).map(record => ({
          label: `Prospect - ${record.companyName}`,
          value: `PROSPECT:${record.id}`,
          type: 'PROSPECT' as SourceType,
          id: record.id,
          record
        }));
        this.crmService.searchLeads({ companyId, size: 500, sortBy: 'companyName', sortDir: 'asc' }).subscribe(leads => {
          const leadOptions = (leads.content || []).map(record => ({
            label: `Lead - ${[record.leadNumber, record.companyName].filter(Boolean).join(' - ') || record.id}`,
            value: `LEAD:${record.id}`,
            type: 'LEAD' as SourceType,
            id: record.id,
            record
          }));
          this.sourceOptions.set([...clientOptions, ...prospectOptions, ...leadOptions]);
        });
      });
    });
  }

  private syncBrandSelectionFromName() {
    const currentBrandId = this.posmForm.get('brandId')?.value;
    const brandName = (this.posmForm.get('brandName')?.value || '').trim().toLowerCase();
    if (currentBrandId || !brandName) return;
    const match = this.brandOptions().find(brand => brand.name.trim().toLowerCase() === brandName);
    if (match?.id) {
      this.posmForm.patchValue({ brandId: match.id }, { emitEvent: false });
    }
  }

  private showValidationWarning(...forms: FormGroup[]) {
    forms.forEach(form => form.markAllAsTouched());
    this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'There are validation errors. Please review the highlighted fields.' });
  }

  private showValidationWarningWithDetail(detail: string, ...forms: FormGroup[]) {
    forms.forEach(form => form.markAllAsTouched());
    this.messageService.add({ severity: 'warn', summary: 'Validation', detail });
  }

  canConvertToProject() {
    const quote = this.quotation();
    return !!quote && this.isPosmQuote(quote) && (quote.status === 'APPROVED' || quote.status === 'JOB') && !!quote.posmDetails?.projectName;
  }

  canCreateContract() {
    const status = this.quotation()?.status;
    return status === CrmQuotationStatus.APPROVED || status === CrmQuotationStatus.JOB || status === CrmQuotationStatus.COMPLETED;
  }

  canCreateSalesOrder() {
    return this.canCreateContract();
  }

  isPosmQuote(quote: CrmQuotation | null = this.quotation()) {
    return !!quote && (quote.type === 'POSM' || quote.jobType === CrmQuotationJobType.POSM_INSTALLATION || quote.jobType === CrmQuotationJobType.POSM_REFURBISHMENT);
  }

  isLogisticsQuote(quote: CrmQuotation | null = this.quotation()) {
    return !!quote && (
      quote.jobType === CrmQuotationJobType.LOGISTICS
      || quote.jobType === CrmQuotationJobType.POSM_INSTALLATION
      || (!quote.jobType && quote.type === 'LOGISTICS')
    );
  }

  isCfQuote(quote: CrmQuotation | null = this.quotation()) {
    return !!quote && (
      quote.jobType === CrmQuotationJobType.CUSTOMS_CLEARANCE
      || quote.jobType === CrmQuotationJobType.CF_IMPORT_AIR
      || quote.jobType === CrmQuotationJobType.CF_IMPORT_SEA
      || quote.jobType === CrmQuotationJobType.CF_IMPORT_LAND
      || quote.jobType === CrmQuotationJobType.CF_EXPORT
      || quote.jobType === CrmQuotationJobType.CF_RORO
      || quote.jobType === CrmQuotationJobType.CF_CONSOLIDATION
    );
  }

  isStorageQuote(quote: CrmQuotation | null = this.quotation()) {
    return !!quote && (
      quote.jobType === CrmQuotationJobType.STORAGE
      || quote.jobType === CrmQuotationJobType.POSM_INSTALLATION
      || quote.type === 'STORAGE'
    );
  }

  getSeverity(status?: string) {
    switch (status) {
      case 'COMPLETED':
      case 'APPROVED':
      case 'JOB':
        return 'success';
      case 'SUBMITTED':
      case 'CLIENT_REVIEW':
      case 'READY_TO_SUBMIT':
        return 'info';
      case 'PREPARING':
      case 'INTERNAL_REVIEW':
      case 'REVISION_REQUESTED':
        return 'warn';
      case 'REJECTED':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  rateLane(rate: LogisticsRateCardRow) {
    return `${this.joinParts(rate.sourceCity, rate.sourceCountry) || '-'} -> ${this.joinParts(rate.destinationCity, rate.destinationCountry) || '-'}`;
  }

  rateVehicle(rate: LogisticsRateCardRow) {
    return this.joinParts(rate.vehicleTypeName, rate.vehicleConfigName, rate.vehicleCapacityName) || '-';
  }

  rateIncluded(value?: boolean) {
    return value === true ? 'Yes' : value === false ? 'No' : '-';
  }

  loadQuotation(id: number) {
    this.loading.set(true);
    this.crmService.getQuotation(id).subscribe({
      next: (response) => {
        if (response.success) {
          this.quotation.set(response.data);
          this.patchForms(response.data);
          this.loadQuotationWork(response.data.id!);
          const convertedClientId = Number(this.route.snapshot.queryParamMap.get('convertedClientId'));
          if (convertedClientId && response.data.clientId !== convertedClientId) {
            this.relinkQuotationToConvertedClient(response.data, convertedClientId);
          }
        }
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  patchForms(data: CrmQuotation) {
    this.generalForm.patchValue({
      subject: data.subject,
      description: data.description || '',
      countryId: data.countryId ?? null,
      cityId: data.cityId ?? null,
      sourceKey: this.sourceKey(data),
      customerName: data.customerName,
      customerId: data.customerId ?? null,
      pocName: data.pocName,
      pocEmail: data.pocEmail,
      pocPhoneNumber: data.pocPhoneNumber || '',
      pocMobileNumber: data.pocMobileNumber || '',
      currencyCode: data.currencyCode || 'SAR',
      exchangeRate: data.exchangeRate || 1,
      vatRate: data.vatRate || 15,
      pricingNotes: data.pricingNotes || '',
      status: data.status
    });
    this.loadCitiesForCountry(data.countryId ?? null);
    this.linesForm.clear();
    (data.lines?.length ? data.lines : [{} as any]).forEach(line => this.linesForm.push(this.createLine(line)));

    if (data.posmDetails) {
      this.posmForm.patchValue({
        projectName: data.posmDetails.projectName,
        eventLocation: data.posmDetails.eventLocation,
        brandName: data.posmDetails.brandName,
        jobPriority: data.posmDetails.jobPriority,
        jobType: data.posmDetails.jobType
      });
      this.syncBrandSelectionFromName();
    }
    this.ensureSubmitRecipient(data);
  }

  saveDetails() {
    const quote = this.quotation();
    if (!quote) return;

    if (this.contactResolving()) {
      this.messageService.add({ severity: 'info', summary: 'Contact', detail: 'Resolving the quotation contact. Please save again once the contact is selected.' });
      return;
    }

    if (this.generalForm.invalid || (this.isPosmQuote(quote) && this.posmForm.invalid)) {
      this.showValidationWarning(this.generalForm, this.posmForm);
      return;
    }

    const { countryId: _countryId, ...generalValues } = this.generalForm.value;
    const { brandId: _brandId, ...posmValues } = this.posmForm.value as any;
    const selectedBrand = this.brandOptions().find(brand => brand.id === _brandId);

    const updatedQuote: CrmQuotation = {
      ...quote,
      subject: generalValues.subject || quote.subject,
      description: generalValues.description || '',
      cityId: generalValues.cityId ?? null,
      customerName: generalValues.customerName || quote.customerName,
      customerId: generalValues.customerId || quote.customerId,
      pocName: generalValues.pocName || quote.pocName,
      pocEmail: generalValues.pocEmail || quote.pocEmail,
      pocPhoneNumber: generalValues.pocPhoneNumber || quote.pocPhoneNumber,
      pocMobileNumber: generalValues.pocMobileNumber || quote.pocMobileNumber,
      currencyCode: generalValues.currencyCode || quote.currencyCode || 'SAR',
      exchangeRate: generalValues.exchangeRate || quote.exchangeRate || 1,
      vatRate: generalValues.vatRate || quote.vatRate || 15,
      pricingNotes: generalValues.pricingNotes || quote.pricingNotes,
      status: (generalValues.status as CrmQuotationStatus) || quote.status,
      ...this.sourceIdsFromKey(generalValues.sourceKey),
      posmDetails: this.isPosmQuote(quote) ? {
        ...(quote.posmDetails || {}),
        ...posmValues,
        brandName: selectedBrand?.name || posmValues.brandName || ''
      } as any : undefined,
      logisticsDetails: this.isLogisticsQuote(quote) ? quote.logisticsDetails : undefined,
      posmInstallationDetails: this.isPosmQuote(quote) ? quote.posmInstallationDetails : undefined,
      storageDetails: this.isStorageQuote(quote) ? quote.storageDetails : undefined
    };

    this.crmService.saveQuotation(updatedQuote).subscribe(response => {
      if (response.success) {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Quotation updated successfully' });
        this.quotation.set(response.data);
      }
    });
  }

  saveLines() {
    const quote = this.quotation();
    if (!quote?.id) return;
    if (this.linesForm.invalid) {
      this.showValidationWarning(this.linesForm as unknown as FormGroup);
      return;
    }
    const lines = this.linesForm.value.map((line: any, index: number) => ({
      ...line,
      lineNumber: line.lineNumber || index + 1,
      totalLineAmount: this.lineTotal(line)
    }));
    this.crmService.saveQuotationLines(quote.id, lines as any).subscribe(response => {
      if (response.success) {
        this.quotation.set(response.data);
        this.patchForms(response.data);
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Quotation lines updated successfully.' });
      }
    });
  }

  createLine(value: any = {}) {
    return this.fb.group({
      id: [value.id ?? null],
      quotationId: [value.quotationId ?? this.quotation()?.id ?? null],
      lineNumber: [value.lineNumber ?? null],
      productName: [value.productName ?? ''],
      description: [value.description ?? '', Validators.required],
      itemCategory: [value.itemCategory ?? 'SERVICE', Validators.required],
      uom: [value.uom ?? 'EA'],
      quantity: [value.quantity ?? 1, [Validators.required, Validators.min(0)]],
      unitPrice: [value.unitPrice ?? 0, [Validators.required, Validators.min(0)]],
      unitDiscount: [value.unitDiscount ?? 0],
      taxRate: [value.taxRate ?? this.quotation()?.vatRate ?? 15]
    });
  }

  addLine() {
    this.linesForm.push(this.createLine({ lineNumber: this.linesForm.length + 1 }));
  }

  removeLine(index: number) {
    if (this.linesForm.length === 1) {
      this.linesForm.at(0).reset({ itemCategory: 'SERVICE', uom: 'EA', quantity: 1, unitPrice: 0, unitDiscount: 0, taxRate: this.quotation()?.vatRate ?? 15 });
      return;
    }
    this.linesForm.removeAt(index);
  }

  lineBase(line: any) {
    return Number(line.quantity || 0) * Number(line.unitPrice || 0);
  }

  lineDiscount(line: any) {
    return Number(line.quantity || 0) * Number(line.unitDiscount || 0);
  }

  lineTax(line: any) {
    return Math.max(0, this.lineBase(line) - this.lineDiscount(line)) * Number(line.taxRate || 0) / 100;
  }

  lineTotal(line: any) {
    return Math.round((this.lineBase(line) - this.lineDiscount(line) + this.lineTax(line)) * 100) / 100;
  }

  linesTotals() {
    const lines = this.linesForm.value as any[];
    const subTotal = lines.reduce((sum, line) => sum + this.lineBase(line), 0);
    const discountAmount = lines.reduce((sum, line) => sum + this.lineDiscount(line), 0);
    const vatAmount = lines.reduce((sum, line) => sum + this.lineTax(line), 0);
    return {
      subTotal,
      discountAmount,
      vatAmount,
      totalAmount: subTotal - discountAmount + vatAmount
    };
  }

  moveToSubmission() {
    const quote = this.quotation();
    if (quote) {
      this.crmService.transitionQuotation(quote.id!, 'SUBMIT_INTERNAL', 'Submitted for internal review').subscribe(res => {
        if (res.success) {
            this.quotation.set(res.data);
            this.messageService.add({ severity: 'info', summary: 'Status Updated', detail: 'Quotation moved to internal review' });
        }
      });
    }
  }

  markReady() {
    const quote = this.quotation();
    if (!quote?.id) return;
    this.crmService.transitionQuotation(quote.id, 'MARK_READY', 'Internal enrichment completed').subscribe(res => {
      if (res.success) {
        this.quotation.set(res.data);
        this.messageService.add({ severity: 'success', summary: 'Ready', detail: 'Quotation is ready to submit' });
      }
    });
  }

  submitToClient() {
    const quote = this.quotation();
    if (!quote?.id) return;
    this.ensureSubmitRecipient(quote);
    if (this.submitForm.invalid || !this.submitForm.get('recipientEmail')?.value) {
      this.activeTab.set('communication');
      this.showValidationWarningWithDetail('Select a customer contact with an email before submitting to the client.', this.submitForm);
      return;
    }
    const value = this.submitForm.value;
    this.crmService.submitQuotation(quote.id, value.recipientEmail!, value.expiresAt || undefined, value.comment || undefined).subscribe(res => {
      if (res.success) {
        this.messageService.add({ severity: 'success', summary: 'Submitted', detail: 'External review link created' });
        this.loadQuotation(quote.id!);
      }
    });
  }

  private ensureSubmitRecipient(quote: CrmQuotation) {
    if (this.submitForm.get('recipientCustomerId')?.value && this.submitForm.get('recipientEmail')?.value) return;
    const contactId = quote.customerId ?? quote.contactCustomer?.id ?? null;
    if (contactId && this.customerOptions().some(customer => customer.id === contactId)) {
      this.submitForm.patchValue({ recipientCustomerId: contactId }, { emitEvent: true });
      return;
    }
    const recipientEmail = this.submissionRecipientEmail(quote);
    if (recipientEmail) {
      const match = this.customerOptions().find(customer => (customer.emailAddress || '').toLowerCase() === recipientEmail.toLowerCase());
      if (match?.id) {
        this.submitForm.patchValue({ recipientCustomerId: match.id }, { emitEvent: true });
        return;
      }
      this.submitForm.patchValue({ recipientEmail }, { emitEvent: false });
    }
  }

  private applySubmitRecipient(customerId: number | null | undefined) {
    const customer = this.customerOptions().find(option => option.id === customerId);
    this.submitForm.patchValue({ recipientEmail: customer?.emailAddress || '' }, { emitEvent: false });
  }

  contactLabel(customer: Customer) {
    return [customer.name, customer.emailAddress, customer.phoneNumber].filter(Boolean).join(' - ') || `Customer #${customer.id}`;
  }

  quotationReviewUrl(review: CrmQuotationExternalReview): string {
    if (!review.token) {
      return '';
    }
    return `${environment.restApiUrl.replace(/\/$/, '')}/public/crm/quotation-review/${encodeURIComponent(review.token)}`;
  }

  reviewRecipientCustomer(review: CrmQuotationExternalReview): Customer | undefined {
    const email = (review.recipientEmail || '').toLowerCase();
    if (!email) {
      return undefined;
    }
    return this.customerOptions().find(customer => (customer.emailAddress || '').toLowerCase() === email);
  }

  canShareReviewOnWhatsapp(review: CrmQuotationExternalReview): boolean {
    return !!this.reviewRecipientCustomer(review)?.phoneNumber && !!review.token;
  }

  copyQuotationReviewLink(review: CrmQuotationExternalReview) {
    const url = this.quotationReviewUrl(review);
    if (!url) {
      return;
    }
    this.copyToClipboard(url);
    this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Review link copied.' });
  }

  shareQuotationReviewOnWhatsapp(review: CrmQuotationExternalReview) {
    const url = this.quotationReviewUrl(review);
    const customer = this.reviewRecipientCustomer(review);
    const phone = this.normalizedPhone(customer?.phoneNumber);
    if (!url || !phone) {
      this.messageService.add({ severity: 'warn', summary: 'WhatsApp', detail: 'Recipient phone number is missing.' });
      return;
    }
    const quote = this.quotation();
    const text = [
      `Quotation review link${quote?.quotationNumber ? ` for ${quote.quotationNumber}` : ''}:`,
      url
    ].join(' ');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  }

  cityLabel(city?: City | number | null) {
    if (typeof city === 'number') {
      city = this.cities().find(item => item.id === city);
    }
    return city ? [city.cityName, city.country?.name].filter(Boolean).join(', ') : '';
  }

  cityOptions() {
    const countryId = this.generalForm.get('countryId')?.value;
    if (!countryId) return [];
    return this.cities().filter(city => city.country?.id === countryId);
  }

  onGeneralCountryChange(countryId?: number | null) {
    const cityId = this.generalForm.get('cityId')?.value;
    const city = cityId ? this.cities().find(item => item.id === cityId) : null;
    if (!countryId || (cityId && (!city || city.country?.id !== countryId))) {
      this.generalForm.patchValue({ cityId: null }, { emitEvent: false });
    }
    this.loadCitiesForCountry(countryId);
  }

  private loadCitiesForCountry(countryId?: number | null) {
    if (!countryId || this.loadedCityCountryIds.has(countryId) || this.loadingCityCountryIds.has(countryId)) return;
    this.loadingCityCountryIds.add(countryId);
    this.masterDataService.getCities('en-US', countryId).pipe(
      finalize(() => this.loadingCityCountryIds.delete(countryId))
    ).subscribe(cities => {
      const existingById = new Map(this.cities().map(city => [city.id, city]));
      (cities ?? []).forEach(city => existingById.set(city.id, city));
      this.cities.set(Array.from(existingById.values()).sort((a, b) => (a.cityName || '').localeCompare(b.cityName || '')));
      this.loadedCityCountryIds.add(countryId);
    });
  }

  quotationCityLabel(quote: CrmQuotation | null) {
    if (!quote) return '-';
    return quote.cityName || this.cityLabel(quote.cityId) || '-';
  }

  openContactDialog() {
    this.editingContact.set(null);
    this.contactForm.reset({
      id: null,
      name: this.generalForm.get('pocName')?.value || '',
      emailAddress: this.generalForm.get('pocEmail')?.value || '',
      phoneNumber: this.generalForm.get('pocPhoneNumber')?.value || this.generalForm.get('pocMobileNumber')?.value || ''
    });
    this.contactDialogVisible.set(true);
  }

  openEditRecipientDialog() {
    const customerId = this.submitForm.get('recipientCustomerId')?.value;
    const customer = this.customerOptions().find(option => option.id === customerId);
    if (!customer) {
      this.messageService.add({ severity: 'warn', summary: 'Contact', detail: 'Select a recipient contact before editing.' });
      return;
    }
    this.editingContact.set(customer);
    this.contactForm.reset({
      id: customer.id ?? null,
      name: customer.name || '',
      emailAddress: customer.emailAddress || '',
      phoneNumber: customer.phoneNumber || ''
    });
    this.contactDialogVisible.set(true);
  }

  closeContactDialog() {
    if (this.savingContact()) return;
    this.editingContact.set(null);
    this.contactDialogVisible.set(false);
  }

  createContact() {
    if (this.savingContact()) {
      return;
    }
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Contact name is required.' });
      return;
    }
    const value = this.contactForm.value;
    const existing = this.editingContact();
    const customer = {
      ...(existing || {}),
      id: existing?.id,
      name: (value.name || '').trim(),
      emailAddress: (value.emailAddress || '').trim(),
      phoneNumber: (value.phoneNumber || '').trim(),
      company: existing?.company || this.toolbarService.customer()?.company,
      countryId: existing?.countryId || this.toolbarService.customer()?.company?.countryId,
      corporateRole: existing?.corporateRole || 'CLIENT_CONTACT',
      defaultLanguage: (existing as any)?.defaultLanguage || 'en',
      isPhoneVerified: existing?.isPhoneVerified ?? false,
      rating: (existing as any)?.rating ?? 0
    } as any;
    this.savingContact.set(true);
    const request = existing?.id ? this.customerService.updateCustomer(customer) : this.customerService.createCustomer(customer);
    request.subscribe({
      next: response => {
        this.savingContact.set(false);
        if (!response.success || !response.data) return;
        const savedContact = this.upsertCustomerOption(response.data);
        this.generalForm.patchValue({
          customerId: savedContact.id,
          pocName: savedContact.name,
          pocEmail: savedContact.emailAddress || '',
          pocPhoneNumber: savedContact.phoneNumber || '',
          pocMobileNumber: ''
        }, { emitEvent: false });
        this.submitForm.patchValue({
          recipientCustomerId: savedContact.id ?? null,
          recipientEmail: savedContact.emailAddress || ''
        }, { emitEvent: false });
        this.editingContact.set(null);
        this.contactDialogVisible.set(false);
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: existing?.id ? 'Contact updated successfully.' : 'Contact created successfully.' });
      },
      error: () => this.savingContact.set(false)
    });
  }

  private submissionRecipientEmail(quote: CrmQuotation) {
    return quote.contactCustomer?.emailAddress || quote.pocEmail || '';
  }

  private joinParts(...parts: Array<string | number | null | undefined>) {
    return parts.filter(part => part !== null && part !== undefined && String(part).trim() !== '').join(' / ');
  }

  approveQuotation() {
    const id = this.quotation()?.id;
    const userId = this.toolbarService.customer()?.id;
    if (id && userId) {
      this.crmService.approveQuotation(id, userId).subscribe(res => {
        if (res.success) {
            this.quotation.set(res.data);
            this.messageService.add({ severity: 'success', summary: 'Approved', detail: 'Quotation has been approved' });
        }
      });
    }
  }

  rejectQuotation() {
    const id = this.quotation()?.id;
    const userId = this.toolbarService.customer()?.id;
    const reason = this.rejectReason();
    if (id && userId && reason) {
      this.crmService.rejectQuotation(id, userId, reason).subscribe(res => {
        if (res.success) {
            this.quotation.set(res.data);
            this.showRejectDialog.set(false);
            this.rejectReason.set('');
            this.messageService.add({ severity: 'warn', summary: 'Rejected', detail: 'Quotation has been rejected' });
        }
      });
    }
  }

  downloadPdf() {
    const id = this.quotation()?.id;
    if (!id) return;
    const existing = this.latestGeneratedPdf();
    if (existing?.fileUrl) {
      window.open(existing.fileUrl, '_blank');
      return;
    }
    this.generateAndOpenPdf(false);
  }

  regeneratePdf() {
    this.generateAndOpenPdf(true);
  }

  private generateAndOpenPdf(regenerate: boolean) {
    const id = this.quotation()?.id;
    if (!id) return;
    this.pdfGenerating.set(true);
    this.messageService.add({ severity: 'info', summary: regenerate ? 'Regenerating PDF' : 'Generating PDF', detail: 'Preparing quotation PDF...' });
    this.crmService.generateQuotationPdf(id, regenerate).subscribe({
      next: response => {
        this.pdfGenerating.set(false);
        if (!response.success || !response.data) return;
        this.attachments.set([response.data, ...this.attachments().filter(attachment => attachment.id !== response.data.id)]);
        this.messageService.add({
          severity: 'success',
          summary: 'PDF Ready',
          detail: `Quotation PDF v${response.data.versionNumber || 1} is ready.`
        });
        if (response.data.fileUrl) {
          window.open(response.data.fileUrl, '_blank');
        }
      },
      error: () => {
        this.pdfGenerating.set(false);
        this.messageService.add({ severity: 'error', summary: 'PDF failed', detail: 'Unable to generate quotation PDF.' });
      }
    });
  }

  sendEmail() {
    const id = this.quotation()?.id;
    if (id) {
        this.loading.set(true);
        this.crmService.sendQuotationEmail(id).subscribe(res => {
            this.loading.set(false);
            if (res.success) {
                this.messageService.add({ severity: 'success', summary: 'Sent', detail: 'Quotation email sent to client' });
                this.loadQuotation(id);
            }
        });
    }
  }

  convertToProject() {
    const quote = this.quotation();
    if (!quote || !this.ensureClientSourceBeforeConversion('creating the POSM project')) return;
    const id = quote.id;
    if (id) {
      this.crmService.convertToProject(id).subscribe(res => {
        if (res.success) {
          this.messageService.add({ severity: 'success', summary: 'Converted', detail: 'Quotation converted to POSM Project' });
          this.router.navigate(['/posm/projects', res.data.id], { queryParams: { mode: 'edit' } });
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res.message || 'Conversion failed' });
        }
      });
    }
  }

  createContract() {
    const quote = this.quotation();
    if (!quote || !this.ensureClientSourceBeforeConversion('creating a contract')) return;
    const id = quote.id;
    if (id) {
      this.router.navigate(['/crm/quotations', id, 'contract-conversion'], { queryParams: { mode: 'NEW_CONTRACT' } });
    }
  }

  createAmendment() {
    const id = this.quotation()?.id;
    const contractId = this.contractLinks()[0]?.contractId;
    if (id) {
      this.router.navigate(['/crm/quotations', id, 'contract-conversion'], { queryParams: { mode: 'AMENDMENT', contractId } });
    }
  }

  createSalesOrder() {
    const quote = this.quotation();
    if (!quote || !this.ensureClientSourceBeforeConversion('creating a sales order')) return;
    const id = quote.id;
    if (id) {
      this.router.navigate(['/crm/quotations', id, 'sales-order-conversion']);
    }
  }

  createLogisticsProjectFromQuotation() {
    const quote = this.quotation();
    const companyId = quote?.companyId || this.toolbarService.customer()?.company?.id || this.sessionStorageService.getCompany()?.id;
    if (!quote?.id || !companyId) {
      return;
    }
    this.logisticsProjectsService.createProjectFromQuotation(quote.id, companyId).subscribe({
      next: project => {
        this.messageService.add({ severity: 'success', summary: 'Project created', detail: project.projectNumber || 'Logistics project created.' });
        if (project.projectId) {
          this.router.navigate(['/logistics-projects', project.projectId]);
        }
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Project failed',
        detail: error?.error?.message || 'Unable to create logistics project from quotation.'
      })
    });
  }

  openCfExecutionHandoff() {
    const quote = this.quotation();
    if (!quote?.id || !this.isCfQuote(quote)) {
      return;
    }
    this.cfHandoffVisible.set(true);
    this.loadCfExecutionPreview();
  }

  loadCfExecutionPreview() {
    const quote = this.quotation();
    const companyId = quote?.companyId || this.toolbarService.customer()?.company?.id || this.sessionStorageService.getCompany()?.id;
    if (!quote?.id || !companyId) {
      return;
    }
    this.cfHandoffLoading.set(true);
    this.crmService.previewCfExecutionFromQuotation(quote.id, this.cfHandoffRequest(), companyId)
      .pipe(finalize(() => this.cfHandoffLoading.set(false)))
      .subscribe({
        next: preview => this.cfHandoffPreview.set(preview),
        error: error => this.messageService.add({
          severity: 'error',
          summary: 'Preview failed',
          detail: error?.error?.message || 'Unable to preview C&F execution handoff.'
        })
      });
  }

  createCfExecutionFromQuotation() {
    if (this.cfHandoffExecuting()) {
      return;
    }
    const quote = this.quotation();
    const companyId = quote?.companyId || this.toolbarService.customer()?.company?.id || this.sessionStorageService.getCompany()?.id;
    if (!quote?.id || !companyId) {
      return;
    }
    this.cfHandoffExecuting.set(true);
    this.crmService.createCfExecutionFromQuotation(quote.id, this.cfHandoffRequest(), companyId)
      .pipe(finalize(() => this.cfHandoffExecuting.set(false)))
      .subscribe({
        next: result => {
          this.cfHandoffVisible.set(false);
          this.cfHandoffPreview.set(null);
          this.messageService.add({
            severity: 'success',
            summary: 'C&F execution created',
            detail: result?.project?.projectNumber || 'Execution package is ready.'
          });
          const projectId = result?.project?.projectId || result?.profile?.logisticsProjectId;
          if (projectId) {
            this.router.navigate(['/logistics-projects', projectId], { queryParams: { tab: 'cf-execution' } });
          }
        },
        error: error => this.messageService.add({
          severity: 'error',
          summary: 'C&F handoff failed',
          detail: error?.error?.message || 'Unable to create C&F execution from quotation.'
        })
      });
  }

  cfHandoffRequest() {
    const value = this.cfHandoffForm.value;
    return {
      actorId: this.toolbarService.customer()?.id,
      createSalesOrder: !!value.createSalesOrder,
      paymentTermId: value.paymentTermId || null,
      overrideReason: value.overrideReason || null,
      createClearance: value.createClearance !== false,
      createContainerCase: value.createContainerCase !== false,
      applyTemplate: value.applyTemplate !== false,
      applyDocumentRequirements: value.applyDocumentRequirements !== false
    };
  }

  private ensureClientSourceBeforeConversion(actionLabel: string) {
    const quote = this.quotation();
    if (!quote) return false;
    if (quote.clientId) return true;

    if (quote.leadId) {
      this.redirectToSourceConversion('LEAD', quote.leadId, actionLabel);
      return false;
    }
    if (quote.prospectId) {
      this.redirectToSourceConversion('PROSPECT', quote.prospectId, actionLabel);
      return false;
    }

    this.messageService.add({
      severity: 'warn',
      summary: 'Client Required',
      detail: `Select a CRM client source before ${actionLabel}.`
    });
    return false;
  }

  private redirectToSourceConversion(type: 'LEAD' | 'PROSPECT', id: number, actionLabel: string) {
    this.messageService.add({
      severity: 'warn',
      summary: 'Client Required',
      detail: `${type === 'LEAD' ? 'Lead' : 'Prospect'} must be converted to a client before ${actionLabel}.`
    });
    const route = type === 'LEAD' ? '/crm/leads' : '/crm/prospects';
    this.router.navigate([route, id], {
      queryParams: {
        action: 'convert-to-client',
        returnUrl: this.router.url
      }
    });
  }

  private relinkQuotationToConvertedClient(quote: CrmQuotation, clientId: number) {
    if (!quote.id) return;
    const clientRecord = this.sourceOptions().find(option => option.type === 'CLIENT' && option.id === clientId)?.record as any;
    const updatedQuote = {
      ...quote,
      leadId: null,
      prospectId: null,
      clientId,
      customerName: clientRecord?.companyName || quote.customerName
    } as any as CrmQuotation;

    this.crmService.saveQuotation(updatedQuote).subscribe(response => {
      if (response.success) {
        this.quotation.set(response.data);
        this.patchForms(response.data);
        this.messageService.add({
          severity: 'success',
          summary: 'Client Linked',
          detail: 'Quotation source updated to the converted CRM client.'
        });
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { convertedClientId: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });
  }

  loadQuotationWork(id: number) {
    this.crmService.getQuotationRequirements(id).subscribe(res => this.requirements.set(res.data || []));
    this.crmService.getQuotationTasks(id).subscribe(res => this.tasks.set(res.data || []));
    this.crmService.getQuotationAttachments(id).subscribe(res => this.attachments.set(res.data || []));
    this.crmService.getQuotationReviewNotes(id).subscribe(res => this.reviewNotes.set(res.data || []));
    this.crmService.getQuotationExternalReviews(id).subscribe(res => this.externalReviews.set(res.data || []));
    this.crmService.getQuotationContractLinks(id).subscribe(res => this.contractLinks.set(res.data || []));
    const companyId = this.toolbarService.customer()?.company?.id || this.quotation()?.companyId;
    if (companyId) {
      this.ordersService.getSalesOrdersBySourceQuotation(id, companyId).subscribe(links => this.salesOrderLinks.set(links || []));
    }
  }

  saveRequirement() {
    const quote = this.quotation();
    if (!quote?.id) return;
    if (this.requirementForm.invalid) {
      this.showValidationWarning(this.requirementForm);
      return;
    }
    this.crmService.saveQuotationRequirement(quote.id, this.requirementForm.value as CrmQuotationRequirement).subscribe(res => {
      if (res.success) {
        this.requirements.set([...this.requirements(), res.data]);
        this.requirementForm.reset({ priority: 'NORMAL', status: 'OPEN' });
      }
    });
  }

  saveTask() {
    const quote = this.quotation();
    if (!quote?.id) return;
    if (this.taskForm.invalid) {
      this.showValidationWarning(this.taskForm);
      return;
    }
    this.crmService.saveQuotationTask(quote.id, this.taskForm.value as CrmQuotationTask).subscribe(res => {
      if (res.success) {
        this.tasks.set([...this.tasks().filter(task => task.id !== res.data.id), res.data]);
        this.taskForm.reset({ status: 'OPEN', requiredForSubmission: true });
      }
    });
  }

  updateTaskStatus(task: CrmQuotationTask, status: string) {
    const quote = this.quotation();
    if (!quote?.id) return;
    this.crmService.saveQuotationTask(quote.id, { ...task, status: status as any }).subscribe(res => {
      if (res.success) {
        this.tasks.set(this.tasks().map(item => item.id === res.data.id ? res.data : item));
      }
    });
  }

  onAttachmentFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.selectedAttachmentFile.set(file);
    if (file) {
      this.attachmentForm.patchValue({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      });
    }
  }

  saveAttachment() {
    const quote = this.quotation();
    const file = this.selectedAttachmentFile();
    if (!quote?.id) return;
    if (this.attachmentForm.invalid || !file) {
      this.showValidationWarning(this.attachmentForm);
      return;
    }
    this.uploadingAttachment.set(true);
    const path = `crm/quotations/${quote.id}/${Date.now()}-${file.name}`;
    this.firebaseStorageService.uploadFileAndGetPublicUrl(file, path).subscribe({
      next: fileUrl => {
        const attachment = {
          ...this.attachmentForm.value,
          fileUrl,
          fileName: this.attachmentForm.value.fileName || file.name,
          fileType: file.type,
          fileSize: file.size
        } as CrmQuotationAttachment;
        this.crmService.saveQuotationAttachment(quote.id!, attachment).subscribe(res => {
          this.uploadingAttachment.set(false);
          if (res.success) {
            this.attachments.set([res.data, ...this.attachments()]);
            this.selectedAttachmentFile.set(null);
            this.attachmentForm.reset({ category: 'PROPOSAL', visibility: 'CLIENT_VISIBLE' });
          }
        });
      },
      error: () => {
        this.uploadingAttachment.set(false);
        this.messageService.add({ severity: 'error', summary: 'Upload failed', detail: 'Unable to upload quotation file.' });
      }
    });
  }

  deleteAttachment(attachment: CrmQuotationAttachment) {
    const quote = this.quotation();
    if (!quote?.id || !attachment.id) return;
    this.crmService.deleteQuotationAttachment(quote.id, attachment.id).subscribe(() => {
      this.attachments.set(this.attachments().filter(item => item.id !== attachment.id));
    });
  }

  saveReviewNote() {
    const quote = this.quotation();
    if (!quote?.id) return;
    if (this.noteForm.invalid) {
      this.showValidationWarning(this.noteForm);
      return;
    }
    this.crmService.saveQuotationReviewNote(quote.id, this.noteForm.value as CrmQuotationReviewNote).subscribe(res => {
      if (res.success) {
        this.reviewNotes.set([...this.reviewNotes(), res.data]);
        this.noteForm.reset({ visibility: 'INTERNAL', stakeholderType: 'INTERNAL' });
      }
    });
  }

  private sourceKey(quote: CrmQuotation) {
    if (quote.clientId) return `CLIENT:${quote.clientId}`;
    if (quote.prospectId) return `PROSPECT:${quote.prospectId}`;
    if (quote.leadId) return `LEAD:${quote.leadId}`;
    return null;
  }

  private sourceIdsFromKey(sourceKey?: string | null) {
    const [type, rawId] = (sourceKey || '').split(':');
    const id = Number(rawId);
    return {
      leadId: type === 'LEAD' && id ? id : undefined,
      prospectId: type === 'PROSPECT' && id ? id : undefined,
      clientId: type === 'CLIENT' && id ? id : undefined
    };
  }

  private applySource(sourceKey: string | null) {
    const option = this.sourceOptions().find(item => item.value === sourceKey);
    if (!option) return;
    const record: any = option.record;
    this.generalForm.patchValue({
      customerName: record.companyName || '',
      pocName: record.notifyingPersonName || record.pocName || '',
      pocEmail: record.primaryEmail || record.pocEmail || '',
      pocPhoneNumber: record.phoneNumber || record.pocPhoneNumber || '',
      pocMobileNumber: record.mobileNumber || ''
    }, { emitEvent: false });
    this.resolveContactFromSource(record);
  }

  private applyContact(customerId: number | null) {
    const customer = this.customerOptions().find(item => item.id === customerId);
    if (!customer) return;
    this.selectContactCustomer(customer);
  }

  private resolveContactFromSource(record: any) {
    const contact = this.sourceContact(record);
    const linkedCustomerId = Number(record.linkedCustomerId || 0);
    const localMatch = this.findMatchingCustomer(contact, linkedCustomerId);
    if (localMatch) {
      this.selectContactCustomer(localMatch);
      return;
    }

    if (linkedCustomerId) {
      this.contactResolving.set(true);
      this.customerService.getCustomerById(linkedCustomerId)
        .pipe(finalize(() => this.contactResolving.set(false)))
        .subscribe(response => {
          if (response.success && response.data) {
            this.selectContactCustomer(this.upsertCustomerOption(response.data));
            return;
          }
          this.lookupOrCreateContact(contact);
        });
      return;
    }

    this.lookupOrCreateContact(contact);
  }

  private lookupOrCreateContact(contact: { name: string; email: string; phone: string; countryId: number | null }) {
    if (!contact.name || (!contact.email && !contact.phone)) {
      return;
    }

    const lookup = contact.email
      ? this.customerService.getCustomerByEmailAddress(contact.email)
      : this.customerService.getCustomerByMobileNumber(contact.phone);

    this.contactResolving.set(true);
    lookup.pipe(finalize(() => this.contactResolving.set(false))).subscribe(response => {
      if (response.success && response.data) {
        this.selectContactCustomer(this.upsertCustomerOption(response.data));
        return;
      }
      this.createSourceContact(contact);
    });
  }

  private createSourceContact(contact: { name: string; email: string; phone: string; countryId: number | null }) {
    const currentUser = this.toolbarService.customer();
    const customer = {
      name: contact.name,
      emailAddress: contact.email,
      phoneNumber: contact.phone,
      countryId: contact.countryId || currentUser?.company?.countryId,
      company: currentUser?.company,
      corporateRole: 'CLIENT_CONTACT',
      defaultLanguage: 'en',
      isPhoneVerified: false,
      rating: 0
    } as any;

    this.contactResolving.set(true);
    this.customerService.createCustomer(customer)
      .pipe(finalize(() => this.contactResolving.set(false)))
      .subscribe(response => {
        if (response.success && response.data) {
          this.selectContactCustomer(this.upsertCustomerOption(response.data));
          this.messageService.add({ severity: 'success', summary: 'Contact Registered', detail: 'The source contact was added as a customer contact.' });
          return;
        }
        this.retrySourceContactLookup(contact, response.message);
      });
  }

  private retrySourceContactLookup(contact: { name: string; email: string; phone: string; countryId: number | null }, errorMessage?: string) {
    const lookup = contact.email
      ? this.customerService.getCustomerByEmailAddress(contact.email)
      : contact.phone ? this.customerService.getCustomerByMobileNumber(contact.phone) : null;
    if (!lookup) {
      this.messageService.add({ severity: 'warn', summary: 'Contact', detail: errorMessage || 'Unable to register the source contact automatically.' });
      return;
    }
    this.contactResolving.set(true);
    lookup.pipe(finalize(() => this.contactResolving.set(false))).subscribe(response => {
      if (response.success && response.data) {
        this.selectContactCustomer(this.upsertCustomerOption(response.data));
        return;
      }
      this.messageService.add({ severity: 'warn', summary: 'Contact', detail: errorMessage || 'Unable to register the source contact automatically.' });
    });
  }

  private sourceContact(record: any) {
    return {
      name: (record.notifyingPersonName || record.pocName || '').trim(),
      email: (record.primaryEmail || record.pocEmail || '').trim(),
      phone: (record.phoneNumber || record.pocPhoneNumber || record.mobileNumber || '').trim(),
      countryId: record.countryId || this.generalForm.get('countryId')?.value || this.toolbarService.customer()?.company?.countryId || null
    };
  }

  private findMatchingCustomer(contact: { name: string; email: string; phone: string }, linkedCustomerId?: number) {
    const name = contact.name.toLowerCase();
    const email = contact.email.toLowerCase();
    const phone = this.normalizedPhone(contact.phone);
    return this.customerOptions().find(customer =>
      (!!linkedCustomerId && customer.id === linkedCustomerId) ||
      (!!email && (customer.emailAddress || '').trim().toLowerCase() === email) ||
      (!!phone && this.normalizedPhone(customer.phoneNumber) === phone) ||
      (!!name && (customer.name || '').trim().toLowerCase() === name)
    ) || null;
  }

  private selectContactCustomer(customer: Customer) {
    this.generalForm.patchValue({
      customerId: customer.id,
      pocName: customer.name || '',
      pocEmail: customer.emailAddress || '',
      pocPhoneNumber: customer.phoneNumber || '',
      pocMobileNumber: ''
    }, { emitEvent: false });
  }

  private upsertCustomerOption(customer: Customer) {
    const customers = this.customerOptions().filter(item => item.id !== customer.id);
    this.customerOptions.set([...customers, customer].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    return customer;
  }

  private normalizedPhone(phone?: string | null) {
    return (phone || '').replace(/[^0-9]/g, '');
  }

  private copyToClipboard(value: string) {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).catch(() => this.fallbackCopyToClipboard(value));
      return;
    }
    this.fallbackCopyToClipboard(value);
  }

  private fallbackCopyToClipboard(value: string) {
    const input = document.createElement('textarea');
    input.value = value;
    input.setAttribute('readonly', 'true');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  }
}
